import { useEffect, useRef, useState } from 'react'

export interface MediaSelection {
  name: string
  url: string
  source: 'library' | 'device' | 'vault'
  revokeOnCleanup?: boolean
}

type RuntimeStatus = 'idle' | 'loading' | 'ready' | 'starting' | 'running' | 'error'

interface EmulatorHostProps {
  selection: MediaSelection | null
}

declare global {
  interface Window {
    EJS_player?: string
    EJS_gameUrl?: string
    EJS_gameParentUrl?: string
    EJS_core?: string
    EJS_pathtodata?: string
    EJS_gameName?: string
    EJS_startOnLoaded?: boolean
    EJS_threads?: boolean
    EJS_forceLegacyCores?: boolean
    EJS_disableAutoLang?: boolean
    EJS_noAutoFocus?: boolean
    EJS_DEBUG_XX?: boolean
    EJS_defaultOptions?: Record<string, string | number | boolean>
    EJS_ready?: () => void
    EJS_onGameStart?: () => void
    EJS_emulator?: {
      menuOptionChanged?: (option: string, value: string) => void
      gameManager?: {
        simulateInput?: (player: number, control: number, value: number) => void
      }
    }
  }
}

const DATA_PATH = '/emulator/v4.2.3/'

export function EmulatorHost({ selection }: EmulatorHostProps) {
  const mountRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<RuntimeStatus>('idle')
  const [message, setMessage] = useState('Select an open-library item or a file from your device.')
  const [theater, setTheater] = useState(false)

  useEffect(() => {
    const mount = mountRef.current
    if (!mount || !selection) return

    let active = true
    mount.replaceChildren()
    document.getElementById('ejs-loader')?.remove()
    setStatus('loading')
    setMessage('Loading the local VICE x64sc engine…')

    window.EJS_player = '#c64-emulator'
    window.EJS_gameUrl = selection.url
    window.EJS_gameParentUrl = undefined
    window.EJS_core = 'vice_x64sc'
    window.EJS_pathtodata = DATA_PATH
    window.EJS_gameName = selection.name
    window.EJS_startOnLoaded = false
    window.EJS_threads = true
    window.EJS_forceLegacyCores = false
    window.EJS_disableAutoLang = true
    window.EJS_noAutoFocus = false
    window.EJS_DEBUG_XX = true
    window.EJS_defaultOptions = {
      vice_autostart: 'warp',
      vice_autoloadwarp: 'disk_mute',
      vice_warp_boost: 'enabled',
      vice_drive_true_emulation: 'disabled',
      vice_virtual_device_traps: 'enabled',
      vice_physical_keyboard_pass_through: 'enabled',
      keyboardInput: 'enabled',
    }
    window.EJS_ready = () => {
      if (!active) return
      setStatus('ready')
      setMessage('Engine ready. Press Start Emulator.')
    }
    window.EJS_onGameStart = () => {
      if (!active) return
      window.EJS_emulator?.menuOptionChanged?.('keyboardInput', 'enabled')
      setStatus('running')
      setMessage(`${selection.name} is running locally in this browser.`)
    }

    const script = document.createElement('script')
    script.id = 'ejs-loader'
    script.src = `${DATA_PATH}loader.js`
    script.async = true
    script.onerror = () => {
      if (!active) return
      setStatus('error')
      setMessage('The local emulator engine failed to load.')
    }
    document.head.appendChild(script)

    return () => {
      active = false
      script.remove()
      mount.replaceChildren()
      if (selection.revokeOnCleanup) URL.revokeObjectURL(selection.url)
      delete window.EJS_ready
      delete window.EJS_onGameStart
      delete window.EJS_emulator
    }
  }, [selection])

  const activateNativeControl = (labels: readonly string[]) => {
    const root = mountRef.current
    if (!root) return false
    const element = Array.from(root.querySelectorAll<HTMLElement>('button, [role="button"], .ejs_start_button')).find(candidate => {
      const text = candidate.textContent?.trim() ?? ''
      return candidate.classList.contains('ejs_start_button') || labels.some(label => text === label)
    })
    if (!element) return false
    element.click()
    return true
  }

  const start = () => {
    if (activateNativeControl(['Start Game'])) {
      setStatus('starting')
      setMessage('Starting the selected C64 file…')
      return
    }
    setMessage('The start control is still preparing. Try again in a moment.')
  }

  const fullscreen = () => {
    if (!activateNativeControl(['Enter Fullscreen'])) setMessage('Fullscreen becomes available after the engine starts.')
  }

  return (
    <section className={`workbench ${theater ? 'theater' : ''}`} aria-label="C64 emulator workbench">
      <div className="workbench-bar">
        <div>
          <span className={`status-light status-${status}`} aria-hidden="true" />
          <strong>{selection ? selection.name : 'No media selected'}</strong>
          <span className="source-pill">{selection?.source ?? 'idle'}</span>
        </div>
        <div className="workbench-actions">
          <button type="button" onClick={() => setTheater(value => !value)}>{theater ? 'Standard view' : 'Theater view'}</button>
          <button type="button" onClick={fullscreen}>Full screen</button>
        </div>
      </div>
      <div className="screen-shell">
        {!selection && (
          <div className="screen-empty">
            <span className="prompt">READY.</span>
            <h2>Your local C64 workbench</h2>
            <p>Choose a documented library item or open a compatible file from your device. Device files are not sent to this site.</p>
            <span className="cursor" aria-hidden="true" />
          </div>
        )}
        <div ref={mountRef} id="c64-emulator" className={selection ? 'emulator-mount active' : 'emulator-mount'} tabIndex={0} />
      </div>
      <div className="runtime-strip">
        <span aria-live="polite">{message}</span>
        <div className="runtime-badges">
          <span>WASM {typeof WebAssembly !== 'undefined' ? '✓' : '✕'}</span>
          <span>COI {window.crossOriginIsolated ? '✓' : '—'}</span>
          <span>SAB {typeof SharedArrayBuffer !== 'undefined' ? '✓' : '—'}</span>
        </div>
      </div>
      {selection && status !== 'running' && (
        <button className="start-button" type="button" onClick={start} disabled={status === 'loading'}>
          {status === 'loading' ? 'Preparing engine…' : 'Start emulator'}
        </button>
      )}
    </section>
  )
}
